/**
 * PAW Service
 *
 * When USE_GOBODO_API: uses recordStorageService (Gobodo).
 * Otherwise: uses localStorage via mockDataManager (dev fallback).
 */

import { persistedPAWRecords } from './mockData/pawRecords';
import type { PAWRecord } from '../types';
import { getRecordById, getRecordsForObject, createRecord, updateRecord, deleteRecord } from './recordStorageService';
import { USE_GOBODO_API } from '../utils/gobodoUtils';

const PAWRECORD_OBJECT = 'PAWRecord__c';

class PAWService {
  private static instance: PAWService;
  private listeners: Array<() => void> = [];

  static getInstance(): PAWService {
    if (!PAWService.instance) {
      PAWService.instance = new PAWService();
    }
    return PAWService.instance;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  getPAWRecord(recordId: string): PAWRecord | undefined {
    if (USE_GOBODO_API) return undefined;
    return persistedPAWRecords.get().find(r => r.id === recordId);
  }

  async getPAWRecordAsync(recordId: string): Promise<PAWRecord | null> {
    if (USE_GOBODO_API) {
      const record = await getRecordById(PAWRECORD_OBJECT, recordId);
      return record ? (record as PAWRecord) : null;
    }
    return this.getPAWRecord(recordId) ?? null;
  }

  getPAWRecordsByClientId(clientId: string): PAWRecord[] {
    if (USE_GOBODO_API) return [];
    return persistedPAWRecords.get().filter(r => r.clientId === clientId);
  }

  getPAWRecordsByAuditId(csAuditId: string): PAWRecord[] {
    if (USE_GOBODO_API) return [];
    return persistedPAWRecords.get().filter(r => r.csAuditId === csAuditId);
  }

  async getPAWRecordsByAuditIdAsync(csAuditId: string): Promise<PAWRecord[]> {
    if (USE_GOBODO_API) {
      const records = await getRecordsForObject(PAWRECORD_OBJECT);
      return records.filter(
        (r: Record<string, unknown>) => r.csAuditId === csAuditId || r.CSAuditRelField__c === csAuditId
      ) as PAWRecord[];
    }
    return this.getPAWRecordsByAuditId(csAuditId);
  }

  getAllPAWRecords(): PAWRecord[] {
    if (USE_GOBODO_API) return [];
    return persistedPAWRecords.get();
  }

  createPAWRecord(record: Omit<PAWRecord, 'id' | 'createdDate'>): PAWRecord {
    if (USE_GOBODO_API) {
      throw new Error('Use createRecord from recordStorageService or useCreatePAWRecord mutation');
    }
    const allRecords = persistedPAWRecords.get();
    const maxNumber = Math.max(0, ...allRecords.map(r => {
      const num = r.id.replace('PAW-', '');
      return parseInt(num) || 0;
    }));
    const newRecord: PAWRecord = {
      ...record,
      id: `PAW-${String(maxNumber + 1).padStart(6, '0')}`,
      createdDate: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString()
    };
    persistedPAWRecords.add(newRecord);
    this.notifyListeners();
    return newRecord;
  }

  updatePAWRecord(recordId: string, updates: Partial<PAWRecord>): boolean {
    if (USE_GOBODO_API) return false;
    const record = this.getPAWRecord(recordId);
    if (record) {
      persistedPAWRecords.update(recordId, { ...updates, lastModifiedDate: new Date().toISOString() });
      this.notifyListeners();
      return true;
    }
    return false;
  }

  async updatePAWRecordAsync(recordId: string, updates: Partial<PAWRecord>): Promise<boolean> {
    if (USE_GOBODO_API) {
      await updateRecord(PAWRECORD_OBJECT, recordId, updates, { gobodoPropertyPatch: true });
      return true;
    }
    return this.updatePAWRecord(recordId, updates);
  }

  deletePAWRecord(recordId: string): boolean {
    if (USE_GOBODO_API) return false;
    const deleted = persistedPAWRecords.remove(recordId);
    if (deleted) this.notifyListeners();
    return deleted;
  }

  resetToDefaults(): void {
    if (!USE_GOBODO_API) {
      persistedPAWRecords.reset();
      this.notifyListeners();
    }
  }
}

export const pawService = PAWService.getInstance();
